import type { PatientFileCategoryColorToken } from '@/lib/patientFileCategories';
import { HIGHLIGHT_COLORS, type HighlightColor } from '@/utils/fileHighlights';

/**
 * The four markering washes. The nameless defaults still use these tokens.
 * Extra picker colours map onto the nearest wash; the chip uses its own ink.
 */
export const HIGHLIGHT_COLOR_TAG_TOKEN = {
  yellow: 'amber',
  green: 'green',
  blue: 'teal',
  pink: 'coral',
} as const satisfies Record<HighlightColor, PatientFileCategoryColorToken>;

/**
 * Ten picker colours, warm to cool, far enough apart to tell apart at chip size.
 * muted stays out: it is grey and not a markering colour.
 */
export const HIGHLIGHT_TAG_PALETTE = [
  'amber',
  'orange',
  'red',
  'coral',
  'magenta',
  'violet',
  'teal',
  'cyan',
  'green',
  'lime',
] as const;
export type HighlightTagPaletteToken = (typeof HIGHLIGHT_TAG_PALETTE)[number];

const TAG_TOKEN_TO_HIGHLIGHT: Record<HighlightTagPaletteToken, HighlightColor> = {
  amber: 'yellow',
  orange: 'yellow',
  red: 'pink',
  coral: 'pink',
  magenta: 'pink',
  violet: 'blue',
  teal: 'blue',
  cyan: 'blue',
  green: 'green',
  lime: 'green',
};

/** Saturated chip inks. Separate from the pale text wash so small dots stay distinct. */
export const HIGHLIGHT_TAG_INK: Record<HighlightTagPaletteToken, string> = {
  amber: '#f5c400',
  orange: '#f07412',
  red: '#e02323',
  coral: '#ff4f93',
  magenta: '#c4157a',
  violet: '#7a3ff2',
  teal: '#2f7cf6',
  cyan: '#0e9aaa',
  green: '#14944a',
  lime: '#7ea30a',
};

const HIGHLIGHT_TAG_LABEL_KEY: Record<HighlightTagPaletteToken, string> = {
  amber: 'documents.highlights.color_yellow',
  orange: 'settings.highlight_tags.colors.orange',
  red: 'settings.highlight_tags.colors.red',
  coral: 'documents.highlights.color_pink',
  magenta: 'settings.highlight_tags.colors.magenta',
  violet: 'settings.highlight_tags.colors.violet',
  teal: 'documents.highlights.color_blue',
  cyan: 'settings.highlight_tags.colors.cyan',
  green: 'documents.highlights.color_green',
  lime: 'settings.highlight_tags.colors.lime',
};

/** Gul, Grøn, Blå, Pink — the nameless defaults seeded for every clinic. */
export const DEFAULT_HIGHLIGHT_TAG_COLOR_TOKENS = HIGHLIGHT_COLORS.map(
  (color) => HIGHLIGHT_COLOR_TAG_TOKEN[color],
);

export function isHighlightTagPaletteToken(token: string): token is HighlightTagPaletteToken {
  return (HIGHLIGHT_TAG_PALETTE as readonly string[]).includes(token);
}

export function highlightColorForTagToken(token: string): HighlightColor | null {
  if (isHighlightTagPaletteToken(token)) return TAG_TOKEN_TO_HIGHLIGHT[token];
  return null;
}

/** CSS colour for a tag dot. Null for a legacy token such as muted. */
export function highlightTagInkCss(token: string): string | null {
  if (isHighlightTagPaletteToken(token)) return HIGHLIGHT_TAG_INK[token];
  return null;
}

/** i18n key for the colour a person sees: Gul, not the stored token Amber. */
export function highlightTagColorLabelKey(token: string): string {
  if (isHighlightTagPaletteToken(token)) return HIGHLIGHT_TAG_LABEL_KEY[token];
  return `settings.highlight_tags.colors.${token}`;
}

export interface HighlightTagColorSource {
  id: string;
  name: string;
  color_token: string;
  is_archived: boolean;
  sort_order: number;
}

/** Same order as Indstillinger: sort_order, then Danish name, then id. */
export function compareHighlightTags(
  a: Pick<HighlightTagColorSource, 'id' | 'name' | 'sort_order'>,
  b: Pick<HighlightTagColorSource, 'id' | 'name' | 'sort_order'>,
): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  const byName = a.name.localeCompare(b.name, 'da');
  if (byName !== 0) return byName;
  return a.id.localeCompare(b.id);
}

export function activeHighlightTags<T extends HighlightTagColorSource>(tags: readonly T[]): T[] {
  return tags
    .filter((tag) => !tag.is_archived)
    .slice()
    .sort(compareHighlightTags);
}

/** Tags linked to one markering, in the same order as Indstillinger. */
export function highlightTagsInCatalogOrder<T extends HighlightTagColorSource>(
  tags: readonly T[],
  tagIds: readonly string[] | undefined,
): T[] {
  if (!tagIds || tagIds.length === 0) return [];
  const wanted = new Set(tagIds);
  return tags
    .filter((tag) => wanted.has(tag.id))
    .slice()
    .sort(compareHighlightTags);
}

export function activeTagsForHighlightColor<T extends HighlightTagColorSource>(
  tags: readonly T[],
  color: HighlightColor,
): T[] {
  return activeHighlightTags(tags).filter(
    (tag) => highlightColorForTagToken(tag.color_token) === color,
  );
}

/**
 * Wash colour for a markering. The first selected tag in settings order that
 * has a markering ink wins, so the list and the ink stay in one order.
 */
export function highlightColorForSelectedTags(
  tags: readonly HighlightTagColorSource[],
  tagIds: readonly string[],
): HighlightColor | null {
  for (const tag of highlightTagsInCatalogOrder(tags, tagIds)) {
    const color = highlightColorForTagToken(tag.color_token);
    if (color) return color;
  }
  return null;
}

/** Colours already used by another active tag. Archived tags free the colour. */
export function occupiedHighlightTagColors(
  tags: readonly HighlightTagColorSource[],
  exceptId?: string,
): Set<string> {
  const used = new Set<string>();
  for (const tag of tags) {
    if (tag.is_archived) continue;
    if (exceptId && tag.id === exceptId) continue;
    used.add(tag.color_token);
  }
  return used;
}

export function firstFreeHighlightTagColor(
  tags: readonly HighlightTagColorSource[],
): HighlightTagPaletteToken | null {
  const used = occupiedHighlightTagColors(tags);
  return HIGHLIGHT_TAG_PALETTE.find((token) => !used.has(token)) ?? null;
}

export function highlightTagHasDuplicateColors(tags: readonly HighlightTagColorSource[]): boolean {
  const seen = new Set<string>();
  for (const tag of tags) {
    if (tag.is_archived) continue;
    if (seen.has(tag.color_token)) return true;
    seen.add(tag.color_token);
  }
  return false;
}

export function sameHighlightTagIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((id, index) => id === b[index]);
}

/**
 * Name shown on a tag. A blank default stays usable as «Uden navn · Gul».
 * `colorLabel` is the translated markering colour.
 */
export function highlightTagDisplayName(
  tag: { name: string },
  unnamedLabel: string,
  colorLabel: string,
): string {
  const trimmed = tag.name.trim();
  if (trimmed) return trimmed;
  return `${unnamedLabel} · ${colorLabel}`;
}
